import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { ArrowRight, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

const PortfolioPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: "Modern Kitchen Renovation",
      category: "Residential",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
      description: "Complete kitchen transformation featuring custom cabinetry, quartz countertops, and modern appliances."
    },
    {
      id: 2,
      title: "Downtown Office Build-Out",
      category: "Commercial",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
      description: "Modern office space with open floor plan, meeting rooms, and custom millwork."
    },
    {
      id: 3,
      title: "Lakefront Custom Home",
      category: "New Build",
      location: "Edmond, OK",
      image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80",
      description: "Stunning waterfront property featuring floor-to-ceiling windows and custom deck."
    },
    {
      id: 4,
      title: "Heritage Home Restoration",
      category: "Renovation",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80",
      description: "Careful restoration of a historic home maintaining character while adding modern amenities."
    },
    {
      id: 5,
      title: "Luxury Bathroom Remodel",
      category: "Residential",
      location: "Norman, OK",
      image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80",
      description: "Spa-inspired master bathroom with heated floors, soaker tub, and walk-in shower."
    },
    {
      id: 6,
      title: "Restaurant Fit-Out",
      category: "Commercial",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80",
      description: "Complete restaurant build including commercial kitchen, bar area, and custom seating."
    },
    {
      id: 7,
      title: "Second Story Addition",
      category: "Addition",
      location: "Moore, OK",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
      description: "Full second-story addition doubling the home's living space."
    },
    {
      id: 8,
      title: "Basement Entertainment Space",
      category: "Renovation",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80",
      description: "Finished basement with home theater, wet bar, and guest bedroom."
    },
    {
      id: 9,
      title: "Modern Deck & Patio",
      category: "Exterior",
      location: "Oklahoma City, OK",
      image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80",
      description: "Multi-level composite deck with built-in seating and outdoor kitchen."
    }
  ];

  const categories = ["All", ...new Set(projects.map(p => p.category))];
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredProjects = activeCategory === "All" 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-black pt-32 pb-20 relative overflow-hidden">
        {/* Background logo */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: '300px',
            backgroundPosition: 'right bottom',
            backgroundRepeat: 'no-repeat',
            opacity: 0.05,
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Our Work</p>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-6">
              Project <span className="text-orange-500">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Browse our collection of completed projects. Each one represents our commitment 
              to quality craftsmanship and customer satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="bg-zinc-950 border-y border-orange-500/20 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 font-medium whitespace-nowrap transition-colors ${
                  activeCategory === category
                    ? "bg-orange-500 text-white"
                    : "bg-zinc-800 text-gray-400 hover:bg-zinc-700 hover:text-white"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="group cursor-pointer"
                onClick={() => setSelectedProject(project)}
              >
                <div className="relative overflow-hidden bg-zinc-800 aspect-[4/3] border border-zinc-700 hover:border-orange-500 transition-colors">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors duration-300 flex items-end">
                    <div className="p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
                        {project.category}
                      </span>
                      <h3 className="text-xl font-bold text-white mt-1">{project.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">{project.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div 
            className="bg-zinc-900 border border-orange-500/30 max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-[400px] object-cover"
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/80 flex items-center justify-center hover:bg-orange-500 transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
            <div className="p-8">
              <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
                {selectedProject.category}
              </span>
              <h2 className="text-3xl font-bold text-white mt-2 mb-2">
                {selectedProject.title}
              </h2>
              <p className="text-gray-500 mb-4">{selectedProject.location}</p>
              <p className="text-gray-300 leading-relaxed text-lg">
                {selectedProject.description}
              </p>
              <div className="mt-8">
                <Link to="/estimator">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8">
                    Start Your Project
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-24 bg-black text-white border-t border-orange-500/20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <img 
            src={LOGO_URL} 
            alt="YGK Construction" 
            className="h-20 w-auto mx-auto mb-8 opacity-60"
          />
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to Join Our <span className="text-orange-500">Portfolio?</span>
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Let's discuss your project and bring your vision to life.
          </p>
          <Link to="/estimator">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold text-lg px-8 py-6 btn-glow">
              Get Free Estimate
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
